import React, { useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import { useQueryClient } from '@tanstack/react-query';
import apiClient from '../../api/apiClient';
import usePublisher from '../../hooks/usePublisher';
import { useSubmitHandler } from '../../hooks/useSubmitHandler';
import * as S from './Styles';
import FileUpload from '../../helper/FileUpload';
import ArrowCircleLeft from '@mui/icons-material/ArrowCircleLeftOutlined';
import Text from '../Texts';
import { personalFields } from './fields';
import * as R from './rules';
import NumberConverter from '../../helper/ConvertPersionNumberToEnglish';
import SubmitButton from '../Button/SubmitButton';
import { FormSkeleton } from '../Feedback/Loaders';

interface FormValues {
  name: string;
  display_name: string;
  national_id: string;
  address: string;
  postal_code: string;
  phone: string;
}

interface EditPersonalIdentityProps {
  onNext: () => void;
  publisherId: number;
}

const EditPersonalIdentity: React.FC<EditPersonalIdentityProps> = ({
  onNext,
  publisherId,
}) => {
  const queryClient = useQueryClient();
  const [backendErrors, setBackendErrors] = useState<any>({});
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  // Fetch publisher data directly
  const { data: publisher, isLoading, isError } = usePublisher(publisherId);

  // استفاده از hook برای مدیریت submit
  const { isSubmitting, handleSubmit } = useSubmitHandler({
    successMessage: 'اطلاعات با موفقیت به‌روزرسانی شد',
    debounceMs: 2000, // 2 ثانیه بین submit ها
    onSuccess: async () => {
      if (publisher) {
        // Invalidate و refetch داده‌های publisher
        await queryClient.invalidateQueries({
          queryKey: ['publisher', publisher.id],
        });
      }

      // تاخیر قبل از navigate
      setTimeout(() => {
        onNext();
      }, 800);
    },
  });

  // NOTE: useFormik must run on every render, so the loading/error returns
  // below sit *after* it. Returning before a hook changes the hook order
  // between renders, which React rejects.
  // `draft` carries only the columns whose edit is awaiting review; anything
  // else falls through to the live value.
  const staged = (column: string) =>
    publisher?.draft && column in publisher.draft
      ? publisher.draft[column]
      : (publisher as Record<string, any> | undefined)?.[column];

  const formik = useFormik({
    // Per field: an edit to an approved identity is held for review, so the
    // plain fields are still the live values. Prefer the staged one where it
    // exists, or a publisher reopening this form sees their change missing.
    initialValues: {
      name: staged('name') || '',
      display_name: staged('display_name') || '',
      national_id: staged('national_id') || '',
      address: staged('address') || '',
      postal_code: staged('postal_code') || '',
      phone: staged('phone') || '',
    },
    enableReinitialize: true,
    validationSchema: Yup.object({
      name: Yup.string().required('نام الزامی است'),
      display_name: Yup.string().required('نام نمایشی الزامی است'),
      national_id: Yup.string()
        .required('کد ملی الزامی است')
        .matches(R.NATIONAL_ID, R.NATIONAL_ID_MESSAGE),
      address: Yup.string().required('آدرس الزامی است'),
      postal_code: Yup.string()
        .required('کد پستی الزامی است')
        .matches(R.POSTAL_CODE, R.POSTAL_CODE_MESSAGE),
      phone: Yup.string()
        .required('تلفن ثابت الزامی است')
        .matches(R.LANDLINE_PHONE, R.LANDLINE_PHONE_MESSAGE),
    }),
    onSubmit: async (values) => {
      // بررسی validation errors
      const errors = await formik.validateForm(values);
      if (Object.keys(errors).length > 0) {
        toast.error('لطفاً تمام فیلدهای الزامی را پر کنید');
        return;
      }

      if (!publisher) return;

      await handleSubmit(async () => {
        setBackendErrors({});

        const formData = new FormData();
        Object.keys(values).forEach((key) => {
          const typedKey = key as keyof typeof values;
          if (values[typedKey] !== null && values[typedKey] !== undefined) {
            formData.append(key, values[typedKey]);
          }
        });
        if (selectedFile) {
          formData.append('national_id_image', selectedFile);
        }

        console.log('Submitting form data:', Object.fromEntries(formData));
        const response = await apiClient.post(
          `/v2/publisher/update/${publisher.id}`,
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          },
        );
        console.log('Update Response:', response.data);
        return response.data;
      });
    },
  });

  if (isLoading) {
    return (
      <S.Container>
        <FormSkeleton fields={6} />
      </S.Container>
    );
  }

  if (isError || !publisher) {
    return (
      <S.Container>
        <S.Description variant="body1">
          خطا در بارگذاری اطلاعات
        </S.Description>
      </S.Container>
    );
  }

  return (
    <S.Container>
      {/* This is the edit form, so it must not say "ایجاد ... جدید". */}
      <S.Title variant="h6">{Text.PersonalIdentty.editTitle}</S.Title>
      <S.Description variant="body1">
        {Text.PersonalIdentty.completedData}
      </S.Description>

      <form onSubmit={formik.handleSubmit}>
        <S.GridContainer>
          {personalFields.map((field) => (
            <S.FieldContainer key={field.name}>
              <S.Label>{field.label}</S.Label>
              <S.StyledTextField
                variant="outlined"
                placeholder={field.placeholder}
                size="small"
                name={field.name}
                value={formik.values[field.name as keyof FormValues]}
                onChange={
                  field.numeric
                    ? (event) =>
                        formik.setFieldValue(
                          field.name,
                          NumberConverter.toNumericOnly(event.target.value),
                        )
                    : formik.handleChange
                }
                onBlur={formik.handleBlur}
                error={
                  !!formik.touched[field.name as keyof FormValues] &&
                  !!formik.errors[field.name as keyof FormValues]
                }
                // Gated on `touched` like `error` above is. Without it the
                // message showed on a field the user had not reached yet, so a
                // freshly loaded form claimed a valid value was invalid.
                helperText={
                  (formik.touched[field.name as keyof FormValues] &&
                    formik.errors[field.name as keyof FormValues]) ||
                  backendErrors[field.name]
                }
              />
            </S.FieldContainer>
          ))}
        </S.GridContainer>

        <S.FieldContainer>
          <S.Label>{Text.PersonalIdentty.ImageIdentity}</S.Label>
          <FileUpload
            onFilesSelected={(files) => {
              if (files.length > 0) {
                setSelectedFile(files[0]);
                toast.success(`فایل ${files[0].name} انتخاب شد`);
              }
            }}
            imageSrc="/src/assets/images/identity/paper-upload.svg"
            uploadText={Text.PersonalIdentty.uploadImage}
          />
        </S.FieldContainer>

        <S.SubmitButtonBox>
          <SubmitButton
            type="submit"
            startIcon={<ArrowCircleLeft />}
            isSubmitting={isSubmitting}
            loadingText="در حال ارسال..."
          >
            {Text.PersonalIdentty.submite}
          </SubmitButton>
        </S.SubmitButtonBox>
      </form>
    </S.Container>
  );
};

export default EditPersonalIdentity;
